import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Download,
  Expand,
  FileText,
  ImageIcon,
  LoaderCircle,
  Paperclip,
  Trash2,
  X,
} from 'lucide-react';
import type { HqMonthlyMeetingAttachment } from '@kop/contracts';
import type { HqMonthlyMeetingsRepository } from './repository';
import { useHqMedia } from '../../runtime/use-hq-media';

type AttachmentProps = {
  meetingId: string;
  attachment: HqMonthlyMeetingAttachment;
  repository: HqMonthlyMeetingsRepository;
};

export function MeetingPhoto({ meetingId, attachment, repository }: AttachmentProps) {
  const { host, asset, failed, renew } = useHqMedia(repository, meetingId, attachment.manifestId);
  return asset && !failed ? (
    <img
      src={asset.url}
      alt={attachment.fileName}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={renew}
    />
  ) : (
    <span ref={host} className="hqmm-photo-loading">
      {failed ? (
        <>
          <ImageIcon size={23} />
          사진을 불러오지 못했습니다
        </>
      ) : (
        <>
          <LoaderCircle size={22} className="hqmm-spin" />
          사진 불러오는 중
        </>
      )}
    </span>
  );
}

export function MeetingPhotoSpotlight({
  meetingId,
  title,
  attachments,
  repository,
  onOpen,
}: {
  meetingId: string;
  title: string;
  attachments: readonly HqMonthlyMeetingAttachment[];
  repository: HqMonthlyMeetingsRepository;
  onOpen: (attachment: HqMonthlyMeetingAttachment) => void;
}) {
  const photos = attachments.filter((row) => row.kind === 'photo');
  if (!photos.length) return null;
  return (
    <section className="hqmm-spotlight" aria-label="회의 사진">
      <header>
        <h2>
          <ImageIcon size={17} />
          회의 사진 <span>{photos.length}</span>
        </h2>
        <small>사진을 누르면 크게 볼 수 있습니다</small>
      </header>
      <div className={`hqmm-photo-stage ${photos.length === 1 ? 'hqmm-photo-stage--single' : ''}`}>
        {photos.slice(0, 2).map((photo, index) => (
          <button
            type="button"
            key={photo.manifestId}
            className="hqmm-photo-tile"
            onClick={() => onOpen(photo)}
            aria-label={`${title} 사진 ${index + 1} 확대`}
          >
            <MeetingPhoto
              key={`${meetingId}:${photo.manifestId}`}
              meetingId={meetingId}
              attachment={photo}
              repository={repository}
            />
            <span className="hqmm-photo-caption">
              <strong>{title}</strong>
              <span>{photo.fileName}</span>
            </span>
            <Expand size={18} className="hqmm-photo-expand" />
          </button>
        ))}
      </div>
      {photos.length > 2 && (
        <div className="hqmm-photo-rest">
          {photos.slice(2).map((photo, index) => (
            <button type="button" key={photo.manifestId} onClick={() => onOpen(photo)}>
              <ImageIcon size={15} />
              사진 {index + 3}
              <Expand size={12} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export function MeetingAttachmentList({
  attachments,
  onOpen,
  onRemove,
}: {
  attachments: readonly HqMonthlyMeetingAttachment[];
  onOpen: (attachment: HqMonthlyMeetingAttachment) => void;
  onRemove?: (manifestId: string) => void;
}) {
  return (
    <div className="hqmm-attachments">
      {attachments.length ? (
        attachments.map((attachment) => (
          <article key={attachment.manifestId}>
            <span className="hqmm-file-icon">
              {attachment.kind === 'photo' ? <ImageIcon size={23} /> : <FileText size={23} />}
            </span>
            <button type="button" className="hqmm-file-name" onClick={() => onOpen(attachment)}>
              <strong>{attachment.fileName}</strong>
              <small>
                {
                  { photo: '회의 사진', signature: '참석 서명지', document: '회의 자료' }[
                    attachment.kind
                  ]
                }{' '}
                · {(attachment.byteSize / 1024 / 1024).toFixed(1)}MB
              </small>
            </button>
            <button
              type="button"
              className="hqmm-icon-button"
              onClick={() => onOpen(attachment)}
              aria-label={`${attachment.fileName} 열기`}
            >
              <Expand size={16} />
            </button>
            {onRemove && (
              <button
                type="button"
                className="hqmm-icon-button"
                onClick={() => onRemove(attachment.manifestId)}
                aria-label={`${attachment.fileName} 첨부 제외`}
              >
                <Trash2 size={16} />
              </button>
            )}
          </article>
        ))
      ) : (
        <div className="hqmm-empty-small">
          <Paperclip size={22} />
          등록한 자료가 없습니다.
        </div>
      )}
    </div>
  );
}

export function MeetingAttachmentViewer({
  meetingId,
  attachment,
  repository,
  onClose,
}: AttachmentProps & { onClose: () => void }) {
  const panel = useRef<HTMLElement>(null);
  const { asset, failed, renew, retry } = useHqMedia(
    repository,
    meetingId,
    attachment.manifestId,
    true,
  );
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null,
      overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
      if (event.key === 'Tab') {
        const elements = [
            ...(panel.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled),a[href],iframe',
            ) ?? []),
          ],
          first = elements[0],
          last = elements.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown, true);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', keydown, true);
      previous?.focus();
    };
  }, [attachment.manifestId, repository, meetingId, onClose]);
  return createPortal(
    <div
      className="hqmm-lightbox"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section ref={panel} role="dialog" aria-modal="true" aria-label={attachment.fileName}>
        <header>
          <strong>{attachment.fileName}</strong>
          <button
            type="button"
            className="hqmm-icon-button"
            aria-label="첨부 자료 닫기"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </header>
        <div className="hqmm-lightbox-body">
          {failed ? (
            <p role="alert">
              첨부 자료를 열지 못했습니다.{' '}
              <button type="button" onClick={retry}>
                다시 시도
              </button>
            </p>
          ) : !asset ? (
            <p role="status">
              <LoaderCircle size={22} className="hqmm-spin" />
              자료를 불러오는 중입니다.
            </p>
          ) : attachment.kind === 'photo' ? (
            <img
              src={asset.url}
              alt={attachment.fileName}
              referrerPolicy="no-referrer"
              onError={renew}
            />
          ) : (
            <iframe title={attachment.fileName} src={asset.url} referrerPolicy="no-referrer" />
          )}
        </div>
        <footer>
          {asset && (
            <a href={asset.url} target="_blank" rel="noopener noreferrer" download={asset.fileName}>
              <Download size={16} />
              파일 열기·다운로드
            </a>
          )}
          <button type="button" onClick={onClose}>
            닫기
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
